import { useCallback, useEffect } from 'react';

interface LightboxImage {
  src: string;
  alt: string;
  caption?: string;
}

interface LightboxProps {
  images: LightboxImage[];
  index: number | null;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

export default function Lightbox({ images, index, onClose, onPrev, onNext }: LightboxProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (index === null) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    },
    [index, onClose, onPrev, onNext],
  );

  useEffect(() => {
    if (index !== null) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }
  }, [index, handleKeyDown]);

  if (index === null || !images[index]) return null;

  const image = images[index];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-3 md:p-0"
      onClick={onClose}
      onContextMenu={(e) => e.preventDefault()}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-3 right-3 md:top-5 md:right-5 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-all duration-200 cursor-pointer z-10"
        aria-label="Cerrar"
      >
        <i className="ri-close-line text-xl" />
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-3 md:left-6 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-all duration-200 cursor-pointer z-10"
            aria-label="Anterior"
          >
            <i className="ri-arrow-left-line text-lg md:text-xl" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-3 md:right-6 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-all duration-200 cursor-pointer z-10"
            aria-label="Siguiente"
          >
            <i className="ri-arrow-right-line text-lg md:text-xl" />
          </button>
        </>
      )}

      <div className="max-w-6xl w-full mx-4 md:mx-10" onClick={(e) => e.stopPropagation()}>
        <div className="rounded-lg overflow-hidden relative">
          <img
            src={image.src}
            alt={image.alt}
            className="w-full max-h-[65vh] md:max-h-[75vh] object-contain select-none"
            draggable="false"
          />
          {/* Anti-download overlay */}
          <div
            className="absolute inset-0 z-20"
            onContextMenu={(e) => e.preventDefault()}
            aria-hidden="true"
          />
        </div>
        <div className="text-center mt-4">
          {image.caption && (
            <p className="text-white font-heading text-base font-semibold">{image.caption}</p>
          )}
          <p className="text-neutral-400 text-xs mt-1">{image.alt}</p>
        </div>

        {images.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-4">
            {images.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index ? 'bg-white w-5' : 'bg-white/40 w-1.5'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}